import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Award, CheckCircle } from 'lucide-react';

const grantRecipients = [
  '703 Warriors Soccer',
  'All Kids One Field',
  'Athletics & Beyond',
  'Beat the Streets LA',
  'Bridge Lacrosse',
  'Capitol Squash Inc.',
  'Carver High Alumni',
  'DHNS Youth Association',
  'Dixon Fieras FA',
  'Dominion Life Skills',
  'Girls Place Inc.',
  'HEROH Foundation',
  'JCW Foundation Inc.',
  'KEEN Chicago',
  'Oakland Soccer Club',
  'Santa Maria Vortex Baseball',
  'RCX Sports Foundation',
  'SMART Sports Inc.',
  'Somerville Youth Soccer',
  'USAdaptiveRecC'
];

export const FallGrantees = () => {
  return (
    <div className="pt-20" data-testid="fall-grantees-page">
      {/* Header */}
      <section className="py-12 bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link 
            to="/newsroom" 
            className="inline-flex items-center text-[#5B4FF9] hover:text-[#4A3FE8] font-medium mb-6 transition-colors"
            data-testid="back-to-newsroom"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Newsroom
          </Link>
          
          <div className="flex items-center gap-4 text-sm text-zinc-500 mb-4">
            <span className="bg-[#5B4FF9] text-white px-3 py-1 rounded-full text-xs font-semibold">
              Press Release
            </span>
            <span className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              September 3, 2026
            </span>
            <span className="flex items-center">
              <MapPin className="h-4 w-4 mr-1" />
              Minneapolis, MN
            </span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold text-[#272926] leading-tight">
            Players Health Foundation Awards $100,000 in Grants to 20 Youth Sports Organizations Nationwide
          </h1>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Lead */}
          <p className="text-xl text-[#5B4FF9] font-semibold mb-8 leading-relaxed">
            Inaugural grant class reflects the Foundation's mission to protect athletes and expand access to sport in communities nationwide
          </p>

          {/* Body */}
          <div className="prose prose-lg max-w-none text-zinc-700 space-y-6">
            <p>
              <strong>MINNEAPOLIS, MN — September 3, 2026</strong> — The Players Health Foundation today announced the 20 recipients of its 2026 grant program, awarding $5,000 each to youth sports organizations across the country for a total of $100,000 in funding.
            </p>

            <p>
              The grants support a range of athlete safety and development priorities, including equipment, coaching education, safety protocol development, and injury prevention programming. The Foundation directed this round of grants to organizations where the investment can create measurable, scalable impact across entire communities of youth sports participants.
            </p>

            <p>
              Recipients were selected from a nationwide pool of applicants representing a wide range of sports, from soccer and lacrosse to squash, baseball, and adaptive recreation.
            </p>

            {/* Quote 1 */}
            <blockquote className="border-l-4 border-[#5B4FF9] pl-6 py-4 my-8 bg-[#F5F4FF] rounded-r-xl">
              <p className="text-lg italic text-[#272926] mb-4">
                "Every young person deserves the opportunity to enjoy the life-changing benefits of sport in an environment that is safe and supportive. We're proud to invest in the organizations and leaders who are creating stronger, safer sports experiences for young people in communities across the country."
              </p>
              <footer className="text-sm font-semibold text-[#5B4FF9]">
                — Benita Fitzgerald Mosley, Olympic Gold Medalist and Chair, Players Health Foundation
              </footer>
            </blockquote>

            {/* Quote 2 */}
            <blockquote className="border-l-4 border-amber-400 pl-6 py-4 my-8 bg-amber-50 rounded-r-xl">
              <p className="text-lg italic text-[#272926] mb-4">
                "Every kid deserves the chance to find out what sport can do for them. That's what sport gave me growing up, and it's why access has always been at the heart of this Foundation. This first class of grantees is already doing that work in their communities, and we're excited to see the impact they're making today, and even more excited to see what this grant helps them do next."
              </p>
              <footer className="text-sm font-semibold text-amber-600">
                — Tyrre Burks, Players Health Founder and Foundation Board President
              </footer>
            </blockquote>

            <p>
              Grant recipients will participate in Foundation-provided safety training and will share updates on how funding translates into impact for their athletes and communities. The Players Health Foundation plans to open its next grant cycle in 2027. Organizations interested in future funding can learn more at playershealthfoundation.com.
            </p>

            {/* Grant Recipients */}
            <div className="bg-[#5B4FF9] rounded-2xl p-8 my-10">
              <div className="flex items-center gap-3 mb-6">
                <Award className="h-8 w-8 text-white" />
                <h3 className="text-xl font-bold text-white">2026 Fall Grant Recipients</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {grantRecipients.map((recipient, index) => (
                  <div key={index} className="flex items-center text-white/90">
                    <CheckCircle className="h-4 w-4 text-white/70 mr-2 flex-shrink-0" />
                    <span className="text-sm">{recipient}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-zinc-100 rounded-2xl p-8 my-10 text-center">
              <h3 className="text-xl font-bold text-[#272926] mb-4">Interested in Future Funding?</h3>
              <p className="text-zinc-600 mb-6">The Foundation plans to open its next grant cycle in 2027. Learn more about eligibility and how to apply.</p>
              <Link
                to="/grants"
                className="bg-[#5B4FF9] text-white rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-lg inline-flex items-center justify-center"
                data-testid="grantees-learn-more-btn"
              >
                Learn About Our Grant Program
              </Link>
            </div>

            {/* About Section */}
            <div className="border-t border-zinc-200 pt-10 mt-10">
              <h3 className="text-lg font-bold text-[#272926] mb-4">About Players Health Foundation</h3>
              <p className="text-zinc-600 mb-8">
                <strong>Protecting Kids. Powering Possibilities.</strong> Players Health Foundation is a nonprofit dedicated to making youth and amateur sports safer and more accessible. Through grants and free safety resources, the Foundation supports organizations working to protect athletes and expand access to sport in underserved communities. Learn more at{' '}
                <a href="https://playershealthfoundation.com" className="text-[#5B4FF9] hover:underline">
                  playershealthfoundation.com
                </a>.
              </p>

              {/* Media Contact */}
              <div className="bg-zinc-100 rounded-xl p-6">
                <h4 className="text-sm font-bold text-zinc-500 uppercase tracking-wider mb-3">Media Contact</h4>
                <p className="text-[#272926] font-semibold">Kyle Lubrano</p>
                <p className="text-zinc-600 text-sm mb-2">Vice President, Players Health Foundation</p>
                <a href="mailto:kyle.lubrano@playershealth.com" className="text-[#5B4FF9] hover:underline">
                  kyle.lubrano@playershealth.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};
